import React, { useCallback, useRef, useMemo, useEffect } from 'react';
import { useForm, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { MetaAdCampaignObjective, MetaAdCampaignObjectiveInput } from '@frontend/gql/social/graphql';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Form, FormField, FormItem, FormControl, FormMessage } from '@frontend/shadcn/components/ui/form';

// Import shared components from Analytics for selection
import { MembersSelect, MembersOptions } from '../../Analytics/components/filters-form/members-select';
import { Member } from '../../Analytics/components/filters-form/types';

// Import the new filter components
import { AdAccountsOptions } from './ad-accounts-select';
import { AdCampaignsOptions } from './ad-campaigns-select';
import { AdSetsOptions } from './ad-sets-select';
import { AdCampaignObjectivesOptions } from './ad-campaign-objectives-select';

// Canonical set of allowed ad campaign objectives
const ALLOWED_OBJECTIVES = [
  'OUTCOME_APP_PROMOTION',
  'OUTCOME_AWARENESS',
  'OUTCOME_ENGAGEMENT',
  'OUTCOME_LEADS',
  'OUTCOME_SALES',
  'OUTCOME_TRAFFIC',
] as const satisfies readonly MetaAdCampaignObjectiveInput[];

export const adsFiltersFormSchema = z.object({
  adAccountIds: z.array(z.string()).default([]),
  // Stage 3: Split combined field into separate fields for better waterfall filtering
  adCampaignIds: z.array(z.string()).default([]),
  adSetIds: z.array(z.string()).default([]),
  adCampaignObjectives: z.array(z.enum(ALLOWED_OBJECTIVES)).default([]),
  memberIds: z.array(z.string()).default([]),
  compareWithPartnershipAds: z.boolean().default(false),
  showNonAttributedData: z.boolean().default(false),
});

export type AdsFiltersFormData = z.infer<typeof adsFiltersFormSchema>;

interface AdAccount {
  id: string;
  name: string;
}

interface AdCampaign {
  id: string;
  name: string;
  adSets?: AdSet[];
}

interface AdSet {
  id: string;
  name: string;
}

interface AdCampaignObjective {
  id: string;
  name: string;
  objective: MetaAdCampaignObjective;
}

interface AdsFiltersFormProps {
  adAccounts: AdAccount[];
  adCampaigns: AdCampaign[];
  adSets: AdSet[];
  adCampaignObjectives: AdCampaignObjective[];
  isFetchingAdAccounts: boolean;
  isFetchingCampaigns: boolean;
  isFetchingAdSets: boolean;
  isFetchingAdsData: boolean;
  members: Member[];
  setMemberSearchTerm: (term: string) => void;
  isFetchingMembers: boolean;
  defaultValues?: Partial<AdsFiltersFormData>;
  onSubmit: (data: AdsFiltersFormData) => void;
  onCancel: () => void;
  // Callbacks for dynamic waterfall filtering
  onAdAccountIdsChange?: (adAccountIds: string[]) => void;
  onAdCampaignIdsChange?: (adCampaignIds: string[]) => void;
}

export const AdsFiltersForm: React.FC<AdsFiltersFormProps> = ({
  adAccounts,
  adCampaigns,
  adSets,
  adCampaignObjectives,
  isFetchingAdAccounts,
  isFetchingCampaigns,
  isFetchingAdSets,
  isFetchingAdsData,
  members,
  setMemberSearchTerm,
  isFetchingMembers,
  defaultValues,
  onSubmit,
  onCancel,
  onAdAccountIdsChange,
  onAdCampaignIdsChange,
}) => {
  const form = useForm<AdsFiltersFormData>({
    resolver: zodResolver(adsFiltersFormSchema),
    defaultValues: useMemo(
      () => ({
        adAccountIds: defaultValues?.adAccountIds || [],
        adCampaignIds: defaultValues?.adCampaignIds || [],
        adSetIds: defaultValues?.adSetIds || [],
        adCampaignObjectives: defaultValues?.adCampaignObjectives || [],
        memberIds: defaultValues?.memberIds || [],
        compareWithPartnershipAds: defaultValues?.compareWithPartnershipAds || false,
        showNonAttributedData: defaultValues?.showNonAttributedData || false,
      }),
      [defaultValues],
    ),
  });

  // Reset form values when defaultValues change
  // Reset form when defaultValues change - only when form is not open to prevent infinite loops
  useEffect(() => {
    // Only reset when form is not open to prevent infinite loops
    // When form is open, let the user's selections persist
    if (!onAdAccountIdsChange) {
      const newValues = {
        adAccountIds: defaultValues?.adAccountIds || [],
        adCampaignIds: defaultValues?.adCampaignIds || [],
        adSetIds: defaultValues?.adSetIds || [],
        adCampaignObjectives: defaultValues?.adCampaignObjectives || [],
        memberIds: defaultValues?.memberIds || [],
        compareWithPartnershipAds: defaultValues?.compareWithPartnershipAds || false,
        showNonAttributedData: defaultValues?.showNonAttributedData || false,
      };
      form.reset(newValues);
    }
  }, [defaultValues, form, onAdAccountIdsChange]);

  // Data is now provided via props from the useAdsFilterFormData hook

  // Debounce timer reference for search
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search handler for members
  const handleMemberSearch = useCallback(
    (value: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Immediately set empty value without debounce to reset members
      if (!value || value.length === 0) {
        setMemberSearchTerm('');
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        setMemberSearchTerm(value);
      }, 300);
    },
    [setMemberSearchTerm],
  );

  // Watch form values for waterfall logic
  const watchedAdAccountIds = form.watch('adAccountIds');
  const watchedAdCampaignIds = form.watch('adCampaignIds');
  const watchedAdSetIds = form.watch('adSetIds');

  // Form-based waterfall logic
  const areCampaignsEnabled = React.useMemo(() => {
    return !!(watchedAdAccountIds && watchedAdAccountIds.length > 0);
  }, [watchedAdAccountIds]);

  const areAdSetsEnabled = React.useMemo(() => {
    return !!(watchedAdCampaignIds && watchedAdCampaignIds.length > 0);
  }, [watchedAdCampaignIds]);

  // Handle waterfall dependencies - clear child selections when parent is deselected
  React.useEffect(() => {
    // If ad accounts are deselected, clear campaigns and ad sets
    if (!areCampaignsEnabled && (watchedAdCampaignIds.length > 0 || watchedAdSetIds.length > 0)) {
      form.setValue('adCampaignIds', []);
      form.setValue('adSetIds', []);
    }
  }, [areCampaignsEnabled, watchedAdCampaignIds.length, watchedAdSetIds.length, form]);

  React.useEffect(() => {
    // If campaigns are deselected, clear ad sets
    if (!areAdSetsEnabled && watchedAdSetIds.length > 0) {
      form.setValue('adSetIds', []);
    }
  }, [areAdSetsEnabled, watchedAdSetIds.length, form]);

  // Call parent callbacks for dynamic waterfall filtering
  React.useEffect(() => {
    onAdAccountIdsChange?.(watchedAdAccountIds);
  }, [watchedAdAccountIds, onAdAccountIdsChange]);

  React.useEffect(() => {
    onAdCampaignIdsChange?.(watchedAdCampaignIds);
  }, [watchedAdCampaignIds, onAdCampaignIdsChange]);

  const handleSubmit = (data: AdsFiltersFormData) => {
    onSubmit(data);
  };

  const handleError = (errors: FieldErrors<AdsFiltersFormData>) => {
    console.error('Ads filter form validation errors:', errors);
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit, handleError)} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <Form {...form}>
          {/* Ad Accounts Filter */}
          <div className="space-y-2 mb-2 mt-4">
            <Label>Ad Accounts</Label>
            <FormField
              control={form.control}
              name="adAccountIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AdAccountsOptions
                      adAccounts={adAccounts}
                      isLoading={isFetchingAdAccounts}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Ad Campaigns Filter */}
          <div className="space-y-2 mb-2">
            <Label>Ad Campaigns</Label>
            <FormField
              control={form.control}
              name="adCampaignIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AdCampaignsOptions
                      adCampaigns={adCampaigns}
                      isLoading={isFetchingCampaigns}
                      value={field.value}
                      onChange={field.onChange}
                      areCampaignsEnabled={areCampaignsEnabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Ad Sets Filter */}
          <div className="space-y-2 mb-2">
            <Label>Ad Sets</Label>
            <FormField
              control={form.control}
              name="adSetIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AdSetsOptions
                      adSets={adSets}
                      isLoading={isFetchingAdSets}
                      value={field.value}
                      onChange={field.onChange}
                      areAdSetsEnabled={areAdSetsEnabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Ads Objectives Filter */}
          <div className="space-y-2 mb-2">
            <Label>Ads Objectives</Label>
            <FormField
              control={form.control}
              name="adCampaignObjectives"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AdCampaignObjectivesOptions
                      adCampaignObjectives={adCampaignObjectives}
                      isLoading={isFetchingAdsData}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Member Filter */}
          <div className="space-y-2 mb-2">
            <Label>Member</Label>
            <FormField
              control={form.control}
              name="memberIds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <MembersSelect>
                      <MembersOptions
                        members={members || []}
                        isLoading={isFetchingMembers}
                        value={field.value}
                        onChange={field.onChange}
                        onSearch={handleMemberSearch}
                      />
                    </MembersSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Partnership Ads Comparison Switch */}
          <div className="space-y-2 mb-2 hidden">
            <FormField
              control={form.control}
              name="compareWithPartnershipAds"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="compareWithPartnershipAds"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="data-[state=checked]:bg-primary"
                        disabled
                        data-dd-action-name="social-ads-partnership-ads-toggle"
                      />
                      <Label htmlFor="compareWithPartnershipAds" className="text-sm font-normal cursor-pointer">
                        Compare Ads with "Partnership Ads" Toggle on in Meta for Aspire's content
                      </Label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Show Non-Attributed Data Switch */}
          <div className="space-y-2 mb-2">
            <FormField
              control={form.control}
              name="showNonAttributedData"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showNonAttributedData"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="data-[state=checked]:bg-primary"
                        data-dd-action-name="social-ads-show-non-attributed-toggle"
                      />
                      <Label htmlFor="showNonAttributedData" className="text-sm font-normal cursor-pointer">
                        Show data not attributed to Aspire
                      </Label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
      </div>

      <div className="flex justify-start gap-2 p-4 mt-auto mb-4 border-t bg-background mx-[-1.5rem]">
        <Button type="submit">Apply</Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
